import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/api/v1/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token: newRefreshToken } = response.data;
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      } else {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  // [2026-03-22] 내 테넌트 목록 (역할 무관, api_key 포함)
  myTenants: () => api.get('/auth/my-tenants'),
};

// Tenants API
export const tenantsAPI = {
  list: (page = 1, size = 20) =>
    api.get('/admin/tenants', { params: { page, size } }),
  get: (id: string) => api.get(`/admin/tenants/${id}`),
  create: (data: any) => api.post('/admin/tenants', data),
  update: (id: string, data: any) => api.patch(`/admin/tenants/${id}`, data),
  delete: (id: string) => api.delete(`/admin/tenants/${id}`),
  regenerateApiKey: (id: string) =>
    api.post(`/admin/tenants/${id}/regenerate-api-key`),
  regenerateWidgetKey: (id: string) =>
    api.post(`/admin/tenants/${id}/regenerate-widget-key`),
  // [2026-03-22] 요금제 관리
  listPlans: () => api.get('/admin/tenants/plans'),
};

// Documents API (with API Key)
export const documentsAPI = {
  list: (apiKey: string, page = 1, size = 20) =>
    api.get('/tenant/documents', {
      params: { page, size },
      headers: { 'X-API-Key': apiKey },
    }),
  get: (apiKey: string, id: string) =>
    api.get(`/tenant/documents/${id}`, {
      headers: { 'X-API-Key': apiKey },
    }),
  uploadFile: (apiKey: string, file: File, title?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    return api.post('/tenant/documents/upload', formData, {
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  createText: (apiKey: string, title: string, content: string) =>
    api.post(
      '/tenant/documents/text',
      { title, content },
      { headers: { 'X-API-Key': apiKey } }
    ),
  createQA: (apiKey: string, title: string, qaPairs: { question: string; answer: string }[]) =>
    api.post(
      '/tenant/documents/qa',
      { title, qa_pairs: qaPairs },
      { headers: { 'X-API-Key': apiKey } }
    ),
  delete: (apiKey: string, id: string) =>
    api.delete(`/tenant/documents/${id}`, {
      headers: { 'X-API-Key': apiKey },
    }),
  reprocess: (apiKey: string, id: string) =>
    api.post(`/tenant/documents/${id}/reprocess`, null, {
      headers: { 'X-API-Key': apiKey },
    }),
  update: (apiKey: string, id: string, data: { title?: string; content?: string; is_active?: boolean }) =>
    api.patch(`/tenant/documents/${id}`, data, {
      headers: { 'X-API-Key': apiKey },
    }),
  crawl: (apiKey: string, url: string, title?: string, maxDepth: number = 1, maxPages: number = 10) =>
    api.post(
      '/tenant/documents/crawl',
      { url, title, max_depth: maxDepth, max_pages: maxPages },
      { headers: { 'X-API-Key': apiKey } }
    ),
  getChunks: (apiKey: string, id: string) =>
    api.get(`/tenant/documents/${id}/chunks`, {
      headers: { 'X-API-Key': apiKey },
    }),
};

// Conversations API
export const conversationsAPI = {
  list: (apiKey: string, page = 1, size = 20) =>
    api.get('/tenant/chat/conversations', {
      params: { page, size },
      headers: { 'X-API-Key': apiKey },
    }),
  get: (apiKey: string, id: string) =>
    api.get(`/tenant/chat/conversations/${id}`, {
      headers: { 'X-API-Key': apiKey },
    }),
  delete: (apiKey: string, id: string) =>
    api.delete(`/tenant/chat/conversations/${id}`, {
      headers: { 'X-API-Key': apiKey },
    }),
};

// Monitoring API (admin)
export const monitoringAPI = {
  overview: () => api.get('/admin/monitoring/overview'),
  dailyUsage: (days = 30) =>
    api.get('/admin/monitoring/daily-usage', { params: { days } }),
  tenantUsage: () => api.get('/admin/monitoring/tenant-usage'),
  conversations: (page = 1, size = 50, tenantId?: string) =>
    api.get('/admin/monitoring/conversations', {
      params: { page, size, ...(tenantId ? { tenant_id: tenantId } : {}) },
    }),
};

// [2026-03-21] 사용자 관리 API
export const usersAPI = {
  list: (page = 1, size = 20, search?: string) =>
    api.get('/admin/users', { params: { page, size, ...(search ? { search } : {}) } }),
  get: (id: string) => api.get(`/admin/users/${id}`),
  create: (data: any) => api.post('/admin/users', data),
  update: (id: string, data: any) => api.patch(`/admin/users/${id}`, data),
  delete: (id: string) => api.delete(`/admin/users/${id}`),
  resetPassword: (id: string) => api.post(`/admin/users/${id}/reset-password`),
  getTenants: (id: string) => api.get(`/admin/users/${id}/tenants`),
  assignTenant: (userId: string, tenantId: string, role: string) =>
    api.post(`/admin/users/${userId}/tenants`, { tenant_id: tenantId, role }),
  updateTenantRole: (userId: string, tenantId: string, role: string) =>
    api.patch(`/admin/users/${userId}/tenants/${tenantId}`, { role }),
  removeTenant: (userId: string, tenantId: string) =>
    api.delete(`/admin/users/${userId}/tenants/${tenantId}`),
  switchTenant: (tenantId: string) =>
    api.post('/auth/switch-tenant', { tenant_id: tenantId }),
};
